import { MerchantBusinessType } from "@/constants/enum";

// LaunchDarkly feature flag names
export const FEATURE_FLAG_PAZE_ON_COLO2 = "PAZE_ON_COLO2";
export const FEATURE_FLAG_HORIZONTAL_SCROLL_ON_COLO2 =
  "HORIZONTAL_SCROLL_ON_COLO2";
export const FEATURE_FLAG_QUANTUM_METRIC = "2025-quantum-metric";
export const FEATURE_FLAG_LOCALIZATION_ON_COLO2 =
  "SERVICES_FRENCH_LOCALIZATION_ENABLED";
export const DEPOSIT_TAKING_FOR_SERVICES_ENABLED =
  "DEPOSIT_TAKING_FOR_SERVICES_ENABLED";
// Gates the bot-neutralization redirect/read path (early error prevention).
// Read from the resolved merchant's launchDarklyFeatures; see
// isEarlyErrorPreventionEnabled in botNeutralization.ts. Doubles as the name of a
// server-level env mirror: at the proxy/middleware layer no merchant is resolved,
// so setting EARLY_ERROR_PREVENTION_ENABLED=true lets those layers honor a
// fleet-wide rollout of the flag. Unset, the merchant-less layers read as OFF.
export const FEATURE_FLAG_EARLY_ERROR_PREVENTION =
  "EARLY_ERROR_PREVENTION_ENABLED";
export const FEATURE_FLAG_AU_SURCHARGE_WARNING = "HIDE_AU_SURCHARGE_WARNING";

export const DEFAULT_ID = "default";

// Routes
export const ROUTE_HOME = "/";
export const ROUTE_RETAIL = "";
export const ROUTE_SERVICES = "/services";
export const ROUTE_ALL = "/all";
export const ROUTE_SERVICES_ALL = `${ROUTE_SERVICES}/all`;
export const ROUTE_MENU = "/menu";
export const ROUTE_MENU_ALL = `${ROUTE_MENU}/all`;
export const ROUTE_SHOP = "/shop";
export const ROUTE_CATEGORIES = "/categories";
export const ROUTE_CATEGORIES_ALL = `${ROUTE_CATEGORIES}/all`;
export const ROUTE_CONFIRMATION = "/confirmation";
export const ROUTE_NOT_FOUND = "/not-found";
export const ROUTE_PLEASE_WAIT = "/please-wait";
export const ROUTE_GUEST_CHECKOUT = "/guest-checkout";
export const ROUTE_CHECKOUT = "/checkout";
export const ROUTE_CUSTOMER_PROFILE = "/customer-profile";
export const ROUTE_MY_ORDERS = "/my-orders";
export const ROUTE_MY_APPOINTMENTS = "/my-appointments";
export const ROUTE_VERIFY_EMAIL = "/verify-email";
export const ROUTE_UNCATEGORISED = "Uncategorized";
export const ROUTE_GIFTCARD = "/giftcard";
export const ROUTE_ABOUT = "/about";
export const ROUTE_POLICY_RETURNS = "/policy/returns";
export const ROUTE_POLICY_TERMS = "/policy/terms";
export const ROUTE_EXPIRED = "/expired";

export const UNCATEGORISED_CATEGORY_ID = "uncategorised";
export const UNCATEGORISED_CACHE_KEY = "uncategorized";

export const RETAIL_ITEMS_FETCH_LIMIT = "999";

export const GOOGLEMAP_URL = "https://maps.googleapis.com/maps/api/js";
export const PRIVACY_POLICY_URL = "https://www.clover.com/privacy-policy";

// Time constants for calculations
export const TIME_CONSTANTS = {
  MINUTES_PER_HOUR: 60,
  MINUTES_PER_DAY: 1440,
  HOURS_PER_DAY: 24,
  SECONDS_PER_MINUTE: 60,
  MILLISECONDS_PER_SECOND: 1000,
  MILLISECONDS_PER_MINUTE: 60 * 1000, // 60000
} as const;

export const DELIVERY_TIP_SUGGESTIONS = [
  {
    id: "FB00MRRQF32PG",
    name: null,
    percentage: 15,
  },
  {
    id: "08F1QBKDN84ZJ",
    name: null,
    percentage: 18,
  },
  {
    id: "M0GQ9889NJ5Q4",
    name: null,
    percentage: 20,
  },
  {
    id: "TK05FMS88WAX0",
    name: null,
    percentage: 25,
  },
];

// API routes
export const API_ROUTE_APPOINTMENTS = "/api/appointments";

export const EMAIL_TYPO_MAP: Record<string, string> = {
  con: "com",
  vom: "com",
  clm: "com",
  comp: "com",
  col: "com",
  fom: "com",
  cim: "com",
  xom: "com",
  gom: "com",
  ckm: "com",
  come: "com",
  cok: "com",
  cpm: "com",
  comm: "com",
  conm: "com",
  vo: "com",
  ney: "net",
  ner: "net",
  nsu: "net",
  ed: "edu",
  eud: "edu",
  du: "edu",
  ogr: "org",
  or: "org",
  og: "org",
  urg: "org",
  rog: "org",
  couk: "co.uk",
  "co.um": "co.uk",
  "co.u": "co.uk",
  "cp.uk": "co.uk",
};

export const MERCHANT_TYPES = {
  HYBRID: "HYBRID",
  SERVICE: "SERVICE",
  RESTAURANT: "RESTAURANT",
  ESSENTIAL: "ESSENTIAL",
  SERVICE_RETAIL_ONLY: "SERVICE_RETAIL_ONLY",
  RETAIL: "RETAIL",
} as const;

// Product Types
export const PRODUCT_TYPE_SERVICE = "SERVICE";
export const PRODUCT_TYPE_REGULAR = "REGULAR";

// Path Segments
export const PATH_SERVICES = "services";
export const PATH_SHOP = "shop";
export const PATH_MENU = "menu";
export const PATH_CATEGORIES = "categories";

// Template Names
export const TEMPLATE_IMAGE_FOCUSED = "image focused";
export const TEMPLATE_DESCRIPTION_FOCUSED = "description focused";

// Merchant plan labels reported to QuantumMetric (plan event) and rendered on
// the /pip-board diagnostics page. Derived from Merchant.merchantBusinessType
// via MERCHANT_PLAN_BY_BUSINESS_TYPE. Changing a label changes the string that
// QuantumMetric records and the value shown on the board.
export const MERCHANT_PLAN_SERVICE = "Services Growth";
export const MERCHANT_PLAN_RESTAURANT = "Restaurant Growth";
export const MERCHANT_PLAN_RETAIL = "Retail Growth";
export const MERCHANT_PLAN_ESSENTIALS = "Essentials";
// Fallback label when merchantBusinessType is absent or unrecognised.
export const MERCHANT_PLAN_UNKNOWN = "unknown";

// Maps a merchant's business type to its plan label. A hybrid merchant reports
// business type SERVICE ("2") with an item bitmap of 6, so it maps to the
// Services Growth plan. Consumers should fall back to MERCHANT_PLAN_UNKNOWN when
// a type has no entry.
export const MERCHANT_PLAN_BY_BUSINESS_TYPE: Record<
  MerchantBusinessType,
  string
> = {
  [MerchantBusinessType.SERVICE]: MERCHANT_PLAN_SERVICE,
  [MerchantBusinessType.RESTAURANT]: MERCHANT_PLAN_RESTAURANT,
  [MerchantBusinessType.RETAIL]: MERCHANT_PLAN_RETAIL,
  [MerchantBusinessType.ESSENTIAL_MERCHANT]: MERCHANT_PLAN_ESSENTIALS,
};
